import {
	RavenModal,
	RavenInputField,
	toast,
	RavenButton,
	RavenNumberFormat,
	RavenCopyText,
} from '@ravenpay/raven-bank-ui';
import '../bills/airtime/airtimeBillsModalIndex.css';
import '../bills/styles/billsModalIndex.css';
import React, { ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from 'xlsx';
import Skeleton from 'react-loading-skeleton';
import RootState from '../../../../redux/types';
import { downloadReceipts } from '../../../../redux/bills';
import {
	filterEmptyColumns,
	formatMetaData,
	identifyNetwork,
} from '../../../../utils/helper/Helper';
import { trimLongString } from '@ravenpay/raven-bank-ui/dist/esm/helper/helper';
import CopyFragment from '../../../../components/common/CopyFragment';
import { bankboxAPI } from '../../../../redux/bankbox';

interface CardData {
	'Wallet ID': string;
	'Card PAN': string;
	CVV: string;
	'Expiry Date': string;
	'Default PIN': string;
	Color: string;
}

const expectedKeys = [
	'Wallet ID',
	'Card PAN',
	'CVV',
	'Expiry Date',
	'Default PIN',
	'Color',
];

type ButtonColors =
	| 'purple-light'
	| 'error-light'
	| 'info-right'
	| 'white-light'
	| 'blue-light'
	| 'green-light'
	| 'orange-light'
	| 'black-light'
	| 'deep-green'
	| 'deep-green-light'
	| 'error-dark'
	| 'deep-green-dark';

interface BillsModalProps {
	direction?: string;
	amount?: number;
	businessEmail?: string;
	type?: string;
	fee?: number;
	mobileNumber?: string;
	provider?: string;
	reference?: string;
	merchantRef?: string;
	merchant_ref?: string;
	status: string | ReactNode;
	date?: string;
	plan?: string;
	token?: string;
	meterNumber?: string;
	transactionType: string;
	smartCard?: string;
	userId?: string;
	isLoadingDetails?: boolean;
	title?: string;
	visible?: boolean;
	platform?: string;
	onClose: Function;
	btnLabel?: string;
	btnColor?: ButtonColors;
	singleChildren?: any;
	singleObj?: any;
}

const AirtimeBil: React.FC<BillsModalProps> = ({
	direction,
	amount,
	businessEmail,
	type,
	fee,
	mobileNumber,
	singleObj,
	provider,
	reference,
	platform,
	merchantRef,
	transactionType,
	status,
	date,
	plan,
	token,
	meterNumber,
	isLoadingDetails = false,
	smartCard,
	userId,
	title,
	visible,
	onClose,
	btnColor,
	btnLabel,
}) => {
	const dispatch = useDispatch();

	const [showDisputeModal, setShowDisputeModal] = useState(false);
	const [uploadValue, setUploadValue] = useState<any>();
	const [modalType, setModalType] = useState<'bill' | 'dispute'>('bill');
	const [disputeFile, setDisputeFile] = useState<any>();
	const [cardJson, setCardJson] = useState<CardData[]>([]);
	const [description, setDescription] = useState('');
	const { loading } = useSelector((state: RootState) => state.bills);
	const [note, setNote] = useState('');
	const [cardQueue, setCardQueue] = React.useState<CardData[]>([]);

	const [showSkeleton, setShowSkeleton] = useState(true);

	useEffect(() => {
		const timer = setTimeout(() => {
			setShowSkeleton(false);
		}, 5000);

		return () => clearTimeout(timer);
	}, []);

	const [formData, setFormData] = React.useState({
		fname: '',
		lname: '',
		bvn: '',
		nin: '',
		email: '',
		phone: '',
		note: '',
		amount: '',
		transactionType: '',
		device_label: '',
		delivery_location: '',
		delivery_option: '',
		delivery_address: '',
		delivery_lga: '',
		transaction_id: '',
		customer_phone: '',
		delivery_state: '',
		landmark: '',
		purpose: '',
		type: '',
		id: '',
		customer_name: '',
		region: { value: undefined, label: undefined },
		number_of_devices: 0,
		business_address: '',
		fee_category: { value: undefined, label: undefined } as any,
		bank: { value: undefined, label: undefined } as any,
		bank_code: '',
		account_number: '',
		bank_name: '',
		quantity: '',
		status: 'Pending',
		created_at: new Date().toISOString(),
		account_name: '',
		settlement_date: '',
		daily_payout_time: '00:00',
	});

	const handleSubmit = async () => {
		// First, parse the amount and handle empty or invalid values
		const cleanedAmount = amount
			? parseFloat(amount.toString().replace(/[^\d.-]/g, '')) // Convert `amount` to string before calling `replace`
			: 0;

		const val = {
			...formData,
			reference: merchantRef,
			device_label: formData.device_label as unknown as string,
			region: formData.region.label as unknown as string,
			fee_ref: String(formData?.fee_category?.reference),
			bank_name: formData?.bank?.label,
			bank_code: formData?.bank?.value,
			account_number: formData?.account_number,
			account_name: formData?.account_name,
			email: formData.email,
			quantity: formData.number_of_devices,
			business_name: formData.device_label,
			phone: formData.phone,
			amount: cleanedAmount,
			status: 'Pending',
			created_at: new Date().toISOString(),
			transaction_id: formData.id,
			customer_name: `${formData.fname} ${formData.lname}`,
			customer_phone: formData.phone,
			type: transactionType,
			note: formData.note,
		};

		delete val.fee_category;
		delete val.bank;

		try {
			// Send the API request to raise the dispute
			const resp = await dispatch(
				bankboxAPI.raiseDispute(filterEmptyColumns(val) as any)
			);

			//('Payload before dispatch:', val);

			// Handle the response
			if (resp.payload?.status === 'success') {
				// Optionally refresh the data after submission
				await dispatch(
					bankboxAPI.getAllBankBoxRequest({
						per_page: 20,
						page: 1,
					})
				);
				onClose();
			} else {
				// Handle error when submission fails
				console.error('Dispute submission failed:', resp.payload?.message);
			}
		} catch (error) {
			console.error('Error submitting dispute:', error);
			// Optionally show an error message to the user
		}
	};

	const isFormValid = () => {
		// Form is valid if description is filled or file is uploaded
		return note.trim() !== '';
	};

	const handleReceiptDownload = async () => {
		if (merchantRef && merchantRef !== '---') {
			const resultAction = await dispatch(downloadReceipts(merchantRef));

			if (downloadReceipts.fulfilled.match(resultAction)) {
				onClose();
			}
		} else {
			// toast.error('Reference ID is missing or invalid');
		}
	};

	const handleClose = () => {
		// Reset form data and modal state
		setFormData({
			fname: '',
			lname: '',
			bvn: '',
			nin: '',
			email: '',
			phone: '',
			note: '',
			amount: '',
			transactionType: '',
			device_label: '',
			delivery_location: '',
			delivery_option: '',
			delivery_address: '',
			delivery_lga: '',
			transaction_id: '',
			customer_phone: '',
			delivery_state: '',
			landmark: '',
			purpose: '',
			type: '',
			id: '',
			customer_name: '',
			region: { value: undefined, label: undefined },
			number_of_devices: 0,
			business_address: '',
			fee_category: { value: undefined, label: undefined } as any,
			bank: { value: undefined, label: undefined } as any,
			bank_code: '',
			account_number: '',
			bank_name: '',
			quantity: '',
			status: 'Pending',
			created_at: new Date().toISOString(),
			account_name: '',
			settlement_date: '',
			daily_payout_time: '00:00',
		});

		// Reset other relevant states (like uploadValue, cardData, etc.)
		setUploadValue(undefined);
		setCardJson([]);
		setCardQueue([]);
		setNote('');
		setDescription('');
		setModalType('bill'); // Reset modal type to 'transaction'

		onClose(); // Call the onClose prop to close the modal from parent
	};

	const renderContentRow = (
		label: string,
		value?: string | number | ReactNode
	) => {
		if (!value) return null;
		return (
			<div className="bills_details_modal_contents_content_holder">
				<p className="bills_details_modal_contents_content_head">{label}</p>
				<p className="bills_details_modal_contents_content_details">{value}</p>
			</div>
		);
	};

	const renderBillDetails = () => (
		<>
			<p
				onClick={() => {
					// //(
					// 	identifyNetwork(
					// 		formatMetaData(singleObj?.meta_data)?.phone_number || ''
					// 	)
					// );
					// //(formatMetaData(singleObj?.meta_data)?.phone_number);
					// //(singleObj);
					// //(plan);
				}}
				className="bills_details_modal_header"
			>
				{title}
			</p>
			<div className="bills_details_modal_contents_content_holder_wrap">
				{renderContentRow('Direction', direction)}
				{renderContentRow('Amount', RavenNumberFormat(amount))}
				{renderContentRow('Platform', platform)}
				{renderContentRow('Business Email', businessEmail)}
				{renderContentRow('Type', type)}
				{renderContentRow('Fee', fee)}
				{renderContentRow('Mobile No', mobileNumber)}
				{renderContentRow('Meter No', meterNumber)}
				{formatMetaData(singleObj?.meta_data) &&
					renderContentRow(
						'Provider',
						identifyNetwork(formatMetaData(singleObj?.meta_data)?.phone_number)
					)}

				{renderContentRow(
					'Reference',
					reference ? (
						<RavenCopyText
							text={reference}
							copyValue={reference}
							toolTipPosition="top-center"
							trimNum={25}
							clickable
							stopPropagation
							color="deep-green-light"
							copyIconColor="var(--primary-base-color)"
							copyIconBgColor="var(--primary-accent-color-one)"
							copiedBgColor=" var(--primary-base-color)"
						/>
					) : (
						'---'
					)
				)}

				{renderContentRow(
					'Merchant Ref',
					merchantRef ? (
						<RavenCopyText
							text={merchantRef}
							copyValue={merchantRef}
							toolTipPosition="top-center"
							trimNum={25}
							clickable
							stopPropagation
							color="deep-green-light"
							copyIconColor="var(--primary-base-color)"
							copyIconBgColor="var(--primary-accent-color-one)"
							copiedBgColor=" var(--primary-base-color)"
						/>
					) : (
						'---'
					)
				)}

				{renderContentRow('Status', status)}
				{renderContentRow('Date', date)}
				{renderContentRow('Plan', plan)}
				{renderContentRow('Token', token)}
				{renderContentRow('Smart Card', smartCard)}
				{renderContentRow('User ID', userId)}
			</div>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginTop: '2rem',
					gap: '2rem',
				}}
			>
				<RavenButton
					label="Raise Dispute"
					color="deep-green-light"
					// size="small"
					style={{
						flex: 1,
						// padding: '1.6rem 2.4rem !important',
						// borderRadius: '8px',
					}}
					// className="reusable-btn-outline-classname reusable-btn-outline-classname-cancel"
					className="reusable-btn-outline-classname"
					onClick={() => setModalType('dispute')}
				/>

				<RavenButton
					loading={loading}
					// size="big"
					label={'Download Receipt'}
					color={btnColor || 'blue-light'}
					style={{
						flex: 1,
						// padding: '1.6rem 2.4rem !important',
						// borderRadius: '8px',
					}}
					onClick={handleReceiptDownload}
				/>
			</div>
		</>
	);

	const renderDisputeModalContent = () => (
		<div className="transaction-details-modal details-modal-wrap">
			<h5 style={{ fontSize: '20px', marginBottom: '16px' }}>Dispute</h5>
			<div
				style={{
					backgroundColor: 'rgba(247, 247, 247, 1)',
					padding: '2rem',
					borderRadius: '1rem',
					marginBottom: '2rem',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						marginBottom: '1.2rem',
						alignItems: 'center',
					}}
				>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							marginBottom: '1.2rem',
							alignItems: 'center',
						}}
					>
						{' '}
						<span style={{ fontSize: '1.4rem', fontWeight: '400' }}>
							Reference •{' '}
							<>
								<span style={{ marginLeft: '.5rem' }}></span>
							</>
						</span>
						{showSkeleton && isLoadingDetails ? (
							<Skeleton width={100} height={20} /> // Skeleton loader only for the reference value
						) : (
							<span style={{ fontSize: '1.4rem', fontWeight: '400' }}>
								{trimLongString(merchantRef, 25) || '---'}
							</span>
						)}
					</div>

					<RavenButton
						color="deep-green-dark"
						label="Back to Transaction"
						style={{
							outline: 'none',
							fontSize: '1.1rem',
							border: 'none',
							padding: '1rem',
							borderRadius: '2rem',
							cursor: 'pointer',
						}}
						onClick={() => setModalType('bill')}
					></RavenButton>
				</div>
				<div
					style={{
						marginBottom: '1.2rem',
						fontSize: '1.4rem',
						fontWeight: '500',
					}}
				>
					{showSkeleton && isLoadingDetails ? (
						<Skeleton width={120} height={20} /> // Skeleton for the amount
					) : (
						<>
							₦{amount?.toLocaleString()} •{' '}
							<span style={{ color: 'gray' }}>{transactionType || '---'}</span>
						</>
					)}
				</div>
			</div>
			<div style={{ marginBottom: '1.2rem' }}>
				<RavenInputField
					color="black-light"
					label="Discription*"
					labelClassName="my_custom_class"
					labelColor="purple-light"
					onActionClick={function noRefCheck() {}}
					textareaColumn={30}
					textareaRow={5}
					type="textarea"
					value={note}
					// style={{ marginBottom: '1rem' }}
					onChange={(e: any) => {
						setNote(e.target.value);
						setFormData((prev) => ({ ...prev, note: e.target.value }));
					}}
					placeholder="Write your thoughts here..."
				/>
			</div>
			{/* File Upload */}
			<div
				className="form-section"
				style={{ marginBottom: '3.5rem', marginTop: '3rem' }}
			>
				<RavenInputField
					label="Upload a file (optional)"
					type="upload"
					color="purple-light"
					onChange={(e: any) => {
						const file = e?.target?.files?.[0];
						setUploadValue(file);
					}}
				/>
				{!uploadValue && (
					<p style={{ marginTop: '1rem' }}>
						Maximum file size acceptable for upload is 2MB
					</p>
				)}
			</div>
			<RavenButton
				loading={loading}
				color="deep-green-light"
				label="Raise Dispute"
				onClick={handleSubmit}
				disabled={!isFormValid() || !note}
				size="big"
				style={{
					width: '100%',
					// padding: '1.2rem',
					borderRadius: '8px',
				}}
			/>
		</div>
	);

	return (
		<RavenModal
			visble={visible}
			loading={loading}
			className="bills-mol"
			onClose={handleClose}
			onBtnClick={modalType === 'bill' ? handleReceiptDownload : () => {}}
			{...(modalType === 'bill'
				? renderBillDetails()
				: renderDisputeModalContent())}

			// btnColor={modalType === 'bill' ? btnColor : undefined}
			// btnLabel={modalType === 'bill' ? btnLabel : undefined}
		>
			<div className="bills_modal_contents_content_holder_wrap">
				{modalType === 'bill'
					? renderBillDetails()
					: renderDisputeModalContent()}
			</div>
		</RavenModal>
	);
};

export default AirtimeBil;
